import { useMemo, useEffect, useRef } from 'react';
import { useWebsiteData } from '../hooks/useWebsiteData';
import { Link } from '@tanstack/react-router';
import gsap from 'gsap';

type TGridPortfolioProps = {
  currentPortfolio?: string;
};

export const GridPortfolio = ({ currentPortfolio }: TGridPortfolioProps) => {
  const { data: websiteData, isLoading, error } = useWebsiteData();
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredAndRandomizedPortfolios = useMemo(() => {
    if (!websiteData) return [];
    const validPortfolios = Object.keys(websiteData).filter((portfolioId) => {
      const section = websiteData[portfolioId];
      return (
        portfolioId !== currentPortfolio &&
        section &&
        section._metadata &&
        section._images
      );
    });

    // eslint-disable-next-line react-hooks/purity
    const shuffled = [...validPortfolios].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 4);
  }, [websiteData, currentPortfolio]);

  useEffect(() => {
    if (
      isLoading ||
      !containerRef.current ||
      filteredAndRandomizedPortfolios.length === 0
    )
      return;

    const ctx = gsap.context(() => {
      const cards = containerRef.current!.querySelectorAll('.portfolio-card');
      gsap.set(cards, { opacity: 0, y: 50 });

      const observer = new IntersectionObserver(
        (entries) => {
          const intersectingEntries = entries.filter(
            (entry) => entry.isIntersecting
          );
          if (intersectingEntries.length > 0) {
            gsap.to(
              intersectingEntries.map((e) => e.target),
              {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: 'power3.out',
                stagger: 0.15,
                onComplete: () => {
                  intersectingEntries.forEach((entry) =>
                    observer.unobserve(entry.target)
                  );
                },
              }
            );
          }
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );

      cards.forEach((card) => observer.observe(card));
      return () => observer.disconnect();
    }, containerRef);

    return () => ctx.revert();
  }, [isLoading, filteredAndRandomizedPortfolios]);

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 w-64 mb-6 mx-auto"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`space-y-4 ${i === 4 ? 'hidden sm:block lg:hidden' : ''}`}
              >
                <div className="aspect-[226/304] bg-slate-200"></div>
                <div className="h-4 bg-slate-200 w-3/4 mx-auto"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !websiteData || Object.keys(websiteData).length === 0)
    return null;

  return (
    <div
      ref={containerRef}
      className="flex flex-col items-center justify-center p-4 md:p-6 lg:p-10"
    >
      <h3 className="my-[60px] text-2xl tracking-wider font-bold text-slate-800">
        View More Projects
      </h3>

      {/* GRID: 
        - 1 col mobile 
        - 2 cols from 'sm' (640px) up
        - 3 cols from 'lg' (1024px) up 
      */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-10 lg:gap-y-12 w-full max-w-7xl">
        {filteredAndRandomizedPortfolios.map((portfolioId, index) => {
          const section = websiteData[portfolioId];
          const thumbnail = section?._images?.[0];

          return (
            <div
              key={portfolioId}
              // Card visibility:
              // If it's the 4th card, hide it on desktop (3 cols)
              // Show it on sm/md (2 cols) and mobile (1 col)
              className={`portfolio-card w-full ${index === 3 ? 'lg:hidden' : ''}`}
            >
              <Link
                to="/portfolio/$portfolioId"
                params={{ portfolioId }}
                className="group block w-full relative"
              >
                <div className="w-full aspect-[226/304] overflow-hidden bg-slate-100 shadow-xl relative z-10">
                  {thumbnail ? (
                    <img
                      src={thumbnail.secure_url}
                      alt={section._metadata.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-slate-400 text-4xl">📁</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 lg:mt-5 text-center group-hover:opacity-60 transition-opacity duration-300">
                  <h3 className="font-semibold text-base lg:text-lg text-slate-800">
                    {section._images?.[0]?.metadata?.website ?? section._metadata.name.replace(/_/g, ' ')}
                  </h3>
                  <p className="text-xs lg:text-sm text-slate-500 mt-1 uppercase tracking-wider">
                    {section._images?.length || 0} images
                  </p>
                </div>
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
};
